import type { PatientMedicationResponse } from '../../services/api';
import {
  getChartLockedMessage,
  getUserFacingApiMessage,
} from '../../utils/apiErrorDetail';
import {
  formatDateToISO,
  getBusinessToday,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import { isDateOutsideEpisodeWindow } from '../../utils/episodeWindow';
import { getHttpStatus } from '../../utils/httpErrorUtils';

const CREATE_RETRY_ERROR =
  'Unable to create medication order. Please try again.';
const CREATE_PERMISSION_ERROR =
  'You do not have permission to create this medication order.';
const MISSING_ORDER_DATE_REASON = 'Choose an order date to create the order.';
const ORDER_DATE_OUTSIDE_EPISODE_REASON =
  'Order date must fall within the active episode.';

export interface MedicationOrderEligibility {
  eligible: boolean;
  reason?: string;
}

/**
 * DatePicker bounds for a medication order date: the episode window, nothing
 * narrower.
 *
 * Deliberately *not* `getEpisodeDateBounds`, which also caps `maxDate` at today
 * because it exists for logging events that already happened. An order date is
 * the date the order takes effect, and the backend accepts any day in the
 * episode (`_validate_order_date_in_episode`), so capping at today would make
 * in-episode dates unreachable in the picker while the create button still
 * accepted them — and, for an episode that has not started yet, would invert the
 * bounds (`minDate` after `maxDate`) and disable every day in the calendar.
 *
 * Missing episode dates relax the corresponding bound, matching
 * `isDateOutsideEpisodeWindow`, which treats an unknown window as no constraint.
 */
export function getMedicationOrderDateBounds(
  episodeStartDate: string | null | undefined,
  episodeEndDate: string | null | undefined
): { minDate: Date | undefined; maxDate: Date | undefined } {
  return {
    minDate: parseISOToLocalDate(episodeStartDate),
    maxDate: parseISOToLocalDate(episodeEndDate),
  };
}

/**
 * Today, clamped into the episode window — the episode bound is used only when
 * today falls outside it (the EMR-766 case: an episode that ended before today
 * defaults to its last day rather than to a date the backend rejects).
 */
export function getDefaultMedicationOrderDate(
  episodeStartDate: string | null | undefined,
  episodeEndDate: string | null | undefined
): string {
  const today = getBusinessToday();
  const todayDate = parseISOToLocalDate(today);
  if (!todayDate) {
    return today;
  }

  const { minDate, maxDate } = getMedicationOrderDateBounds(
    episodeStartDate,
    episodeEndDate
  );
  if (minDate && todayDate < minDate) {
    return formatDateToISO(minDate);
  }
  if (maxDate && todayDate > maxDate) {
    return formatDateToISO(maxDate);
  }
  return today;
}

export function getMedicationOrderDateDisabledReason(
  orderDate: string,
  episodeStartDate: string | null | undefined,
  episodeEndDate: string | null | undefined
): string | null {
  if (!orderDate) {
    return MISSING_ORDER_DATE_REASON;
  }
  if (isDateOutsideEpisodeWindow(orderDate, episodeStartDate, episodeEndDate)) {
    return ORDER_DATE_OUTSIDE_EPISODE_REASON;
  }
  return null;
}

export function getMedicationOrderCreateErrorMessage(
  errorResponse: unknown
): string {
  // A locked chart answers 403 too, but it is not a missing permission.
  const locked = getChartLockedMessage(errorResponse);
  if (locked) return locked;
  const status = getHttpStatus(errorResponse);
  if (status === 403) {
    return CREATE_PERMISSION_ERROR;
  }
  if (status === 400 || status === 409 || status === 422) {
    return getUserFacingApiMessage(errorResponse, CREATE_RETRY_ERROR);
  }
  return CREATE_RETRY_ERROR;
}

export function getMedicationOrderEligibility(
  medication: PatientMedicationResponse,
  orderDateIso: string
): MedicationOrderEligibility {
  const orderDate = parseISOToLocalDate(orderDateIso);
  const startDate = parseISOToLocalDate(medication.start_date);
  const discontinueDate = parseISOToLocalDate(medication.discontinue_date);

  if (!orderDate || !startDate) {
    return { eligible: false, reason: 'Missing medication dates' };
  }

  if (startDate > orderDate) {
    return { eligible: false, reason: 'Starts after order date' };
  }

  if (discontinueDate && discontinueDate < orderDate) {
    return { eligible: false, reason: 'Discontinued before order date' };
  }

  return { eligible: true };
}
